import { useFirstAudienceId } from '@/features/audiences/hooks/useFirstAudienceId';
import { useSmsAliasConnector } from '@/features/campaigns/v2/Sms/hooks/useSmsAliasConnector';
import { extractSmsSendersFromPrepareParams } from '@/features/campaigns/v2/Sms/Senders/smsSenderListFlowMapper';
import { SmsSender } from '@/features/campaigns/v2/Sms/Senders/types';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useCompanyValues } from '@/providers';
import { PrepareConnectorFlowStepResponse, usePrepareConnectorFlowStepMutation } from '@/services/endpoints/workspace-connectors';
import { translateI18nValues } from '@/utils/translationUtil';
import { useCallback, useEffect, useRef, useState } from 'react';

type PrepareStepResult = PrepareConnectorFlowStepResponse['configuration'] extends infer T
  ? T extends { elements?: { params?: infer Params } }
    ? NonNullable<Params>
    : Record<string, unknown>
  : Record<string, unknown>;

type UseSmsSendersOptions = {
  skip?: boolean;
};

export const useSmsSenders = (options?: UseSmsSendersOptions) => {
  const skip = options?.skip ?? false;
  const workspace = useWorkspace();
  const { audienceId, isLoading: isAudienceLoading, isAudienceSettled } = useFirstAudienceId();
  const { currentLanguage } = useCompanyValues();
  const {
    listFlowId,
    editFlowId,
    deleteFlowId,
    connectorInstanceId,
    isListReady,
    isInsertReady,
    isEditReady,
    isDeleteReady,
    isLoading: isConnectorLoading,
    missingInstance,
  } = useSmsAliasConnector();
  const [prepareStep] = usePrepareConnectorFlowStepMutation();

  const [senders, setSenders] = useState<SmsSender[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [isError, setIsError] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const activeRequestId = useRef(0);

  const missingAudience = isAudienceSettled && !audienceId;
  const canFetch = isListReady || isEditReady || isDeleteReady;

  const prepareAndExtract = useCallback(
    async (flowId: string) => {
      const response = await prepareStep({
        workspaceId: workspace.id,
        flowId,
        audienceId: audienceId!,
        stepId: 'step1',
        stepData: {},
        connectorInstanceId,
      }).unwrap();

      const translated = translateI18nValues(response, currentLanguage);
      const params = translated?.configuration?.elements?.params as PrepareStepResult;
      return extractSmsSendersFromPrepareParams(params as Parameters<typeof extractSmsSendersFromPrepareParams>[0]);
    },
    [audienceId, connectorInstanceId, currentLanguage, prepareStep, workspace.id]
  );

  const fetchSenders = useCallback(
    async (isRefetch = false) => {
      if (skip || !canFetch || !connectorInstanceId || !audienceId) {
        return;
      }

      const requestId = ++activeRequestId.current;

      try {
        if (isRefetch) {
          setIsFetching(true);
        } else {
          setIsLoading(true);
        }
        setIsError(false);

        // Prefer Alias List. Only fall back to Edit/Delete prepare when List flow is missing —
        // an empty List result means no senders (Edit/Delete schemas often have a placeholder option that looks like a row).
        let nextSenders: SmsSender[] = [];

        if (listFlowId) {
          nextSenders = await prepareAndExtract(listFlowId);
        } else if (editFlowId) {
          nextSenders = await prepareAndExtract(editFlowId);
        } else if (deleteFlowId) {
          nextSenders = await prepareAndExtract(deleteFlowId);
        }

        if (requestId !== activeRequestId.current) {
          return;
        }

        setSenders(nextSenders);
      } catch {
        if (requestId === activeRequestId.current) {
          setIsError(true);
        }
      } finally {
        if (requestId === activeRequestId.current) {
          setIsLoading(false);
          setIsFetching(false);
          setHasFetched(true);
        }
      }
    },
    [audienceId, canFetch, connectorInstanceId, deleteFlowId, editFlowId, listFlowId, prepareAndExtract, skip]
  );

  const reload = useCallback((): Promise<void> => fetchSenders(true), [fetchSenders]);

  useEffect(() => {
    if (skip) {
      setSenders([]);
      setIsLoading(false);
      setIsFetching(false);
      setIsError(false);
      setHasFetched(false);
      return;
    }

    if (isConnectorLoading || isAudienceLoading) {
      return;
    }

    if (missingAudience) {
      setSenders([]);
      setIsLoading(false);
      setIsFetching(false);
      setIsError(false);
      setHasFetched(true);
      return;
    }

    if (canFetch) {
      void fetchSenders();
      return;
    }

    setIsLoading(false);
    setHasFetched(true);
  }, [canFetch, fetchSenders, isAudienceLoading, isConnectorLoading, missingAudience, skip]);

  return {
    senders,
    sendersCount: senders.length,
    hasFetched,
    missingAudience,
    missingInstance,
    isLoading: skip ? false : isAudienceLoading || isConnectorLoading || (!missingAudience && (isLoading || !hasFetched)),
    isFetching,
    isError,
    reload,
    isListReady,
    isInsertReady,
    isEditReady,
    isDeleteReady,
    deleteFlowId,
    connectorInstanceId,
  };
};
